/**
 * ChartCard — Collapsible card wrapper for charts
 */

import { ChevronDown, Info } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { m } from "@/paraglide/messages";

interface ChartCardProps {
  title: string;
  tooltip?: string;
  children: ReactNode;
  defaultOpen?: boolean;
}

export function ChartCard({
  title,
  tooltip,
  children,
  defaultOpen = true,
}: ChartCardProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, {
      threshold: 0.1,
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  return (
    <div
      ref={ref}
      className={`rounded-xl border border-border bg-card/50 overflow-hidden transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
    >
      {/* Header row — title button collapses, info button shows description, chevron button collapses */}
      <div className="flex items-stretch hover:bg-muted/30 transition-colors">
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex-1 flex items-center px-4 py-3 text-left min-w-0"
        >
          <p className="font-medium text-sm text-foreground">{title}</p>
        </button>
        {tooltip && (
          <button
            type="button"
            onClick={() => setDescOpen((v) => !v)}
            className="flex items-center justify-center w-9 shrink-0 touch-manipulation text-muted-foreground hover:text-foreground transition-colors"
            aria-label={m.f1_chart_more_info()}
            aria-expanded={descOpen}
          >
            <Info className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center pr-4 pl-2 py-3"
          aria-label={isOpen ? m.f1_chart_collapse() : m.f1_chart_expand()}
        >
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>

      {/* Inline description — shown when ⓘ is tapped */}
      {descOpen && tooltip && (
        <div className="px-4 py-2 bg-muted/30 border-b border-border/40 text-xs text-muted-foreground">
          {tooltip}
        </div>
      )}

      {isOpen && isVisible && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
